import { Amount } from '@fcom/dapi/interfaces';
import { PreciseNumber } from '@fcom/core/utils';

/**
 * Adds up two amounts. Assumes they have a matching currencyCode.
 */
export const sumAmounts = (acc: Amount | null, cur: Amount | null): Amount => {
  const a = acc?.amount || '0';
  const b = cur?.amount || '0';

  return {
    amount: PreciseNumber.fromString(a).add(PreciseNumber.fromString(b)).toString(),
    currencyCode: acc?.currencyCode || cur?.currencyCode || '',
  };
};

/**
 * A single pricing tier: provides `price` (and optionally `originalPrice`) for up to `maxNumberItems` items.
 */
export interface TieredPrice {
  price: Amount;
  originalPrice?: Amount;
  maxNumberItems: number;
}

export interface AccumulatedTieredPrice {
  total: Amount;
  totalOriginal: Amount;
}

/**
 * Accumulates the price for `quantity` newly selected items across pricing tiers.
 *
 * Each tier provides a price for up to `maxNumberItems` items. Items already bought (`alreadyBought`) consume
 * the earlier tier slots first, so the newly selected items are charged at the later tiers. Uses `PreciseNumber`
 * to avoid floating-point rounding errors.
 */
export const accumulateTieredPrice = (
  tiers: TieredPrice[],
  quantity: number,
  alreadyBought = 0
): AccumulatedTieredPrice => {
  const { total, totalOriginal, currencyCode } = (tiers ?? []).reduce(
    (acc, tier) => {
      if (acc.remainingItems <= 0) {
        return acc;
      }

      const pricePerItem = PreciseNumber.fromString(tier.price?.amount ?? '0');
      const originalPricePerItem = PreciseNumber.fromString(tier.originalPrice?.amount ?? tier.price?.amount ?? '0');
      const tierSlots = tier.maxNumberItems;
      const remainingSlotsMinusBoughtItems = Math.max(0, tierSlots - acc.alreadyBought);
      const chargeableItems = Math.min(remainingSlotsMinusBoughtItems, acc.remainingItems);
      const chargeableItemsPrecise = new PreciseNumber(chargeableItems, 0);

      return {
        total: acc.total.add(pricePerItem.multiply(chargeableItemsPrecise)),
        totalOriginal: acc.totalOriginal.add(originalPricePerItem.multiply(chargeableItemsPrecise)),
        remainingItems: acc.remainingItems - chargeableItems,
        alreadyBought: Math.max(0, acc.alreadyBought - tierSlots),
        currencyCode: acc.currencyCode ?? tier.price?.currencyCode,
      };
    },
    {
      total: PreciseNumber.fromString('0.00'),
      totalOriginal: PreciseNumber.fromString('0.00'),
      remainingItems: quantity,
      alreadyBought,
      currencyCode: tiers?.[0]?.price?.currencyCode,
    }
  );

  return {
    total: { currencyCode, amount: total.toString() },
    totalOriginal: { currencyCode, amount: totalOriginal.toString() },
  };
};
